import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IMeetingsService } from "services/MeetingsService";
import { IUserSettingsService } from "services/UserSettingsService";
import { MeetingFiltersModel, MeetingModel } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { SETTING_TYPES } from "services/userSettings/SettingTypes";
import { container } from "setup/dependency/inversify.config";

const initialState = {
  Meetings: new Array<MeetingModel>(),
  SavedColumnOrder: Array<string>(),
  ShowPastMeetings: false,
  ShowAllMeetings: false,
  UserID: "",
  LoadingMeetings: false,
};

export const meetingsTableSlice = createSlice({
  name: "meetings",
  initialState,
  reducers: {
    setUserID: (state, action) => {
      state.UserID = action.payload;
    },
    setShowPastMeetings: (state, action) => {
      state.ShowPastMeetings = action.payload;
    },
    setShowAllMeetings: (state, action) => {
      state.ShowAllMeetings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMeetings.pending, (state) => {
        state.LoadingMeetings = true;
      })
      .addCase(getMeetings.fulfilled, (state, action) => {
        state.Meetings = action.payload;
        state.LoadingMeetings = false;
      })
      .addCase(getMeetings.rejected, (state) => {
        state.LoadingMeetings = false;
      })
      .addCase(getMeetingsTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.payload;
      });
  },
});

export const getMeetings = createAsyncThunk<MeetingModel[], MeetingFiltersModel>(
  "meetings/GetMeetings",
  async (meetingFilters: MeetingFiltersModel) => {
    const meetingsService = container.get<IMeetingsService>(SERVICE_TYPES.MeetingsService);
    return await meetingsService.getMeetings(meetingFilters);
  }
);

export const getMeetingsTableColumnOrder = createAsyncThunk("meetings/GetMeetingsTableColumnOrder", async () => {
  const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
  return await userSettingsService.getColumnOrder(SETTING_TYPES.MeetingsTableColumnKey);
});

export const updateMeetingsTableColumnOrder = createAsyncThunk(
  "meetings/UpdateMeetingsTableColumnOrder",
  async (tableColumnOrder: string[]) => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    await userSettingsService.setColumnOrder(tableColumnOrder, SETTING_TYPES.MeetingsTableColumnKey);
  }
);

export const { setShowPastMeetings, setShowAllMeetings, setUserID } = meetingsTableSlice.actions;

export const selectMeetings = (state: RootState) => state.meetings.Meetings;
export const selectLoadingMeetings = (state: RootState) => state.meetings.LoadingMeetings;
export const selectShowPastMeetings = (state: RootState) => state.meetings.ShowPastMeetings;
export const selectShowAllMeetings = (state: RootState) => state.meetings.ShowAllMeetings;
export const selectSavedColumnOrder = (state: RootState) => state.meetings.SavedColumnOrder;
export const selectUserID = (state: RootState) => state.meetings.UserID;

export default meetingsTableSlice.reducer;
